import Konva from 'konva';
import { characterTokenAtPointer, characterTokenGeometry } from './character_token_geometry.js';
import { createCharacterTokenShape } from './character_token_shape.js';
import { SHAPE_DEFAULTS } from './constants.js';

export const CharacterTokenMethods = {
  _openRequestedCharacter() {
    if (!this.hasRequestedCharacterValue || this._requestedCharacterOpened || this.readonlyValue) return;
    this._requestedCharacterOpened = true;
    const character = this.requestedCharacterValue;
    const token = this._editableShapes().find(
      (shape) => shape.type === 'character' && shape.placeableId === character.placeableId
    );
    if (token) {
      this._showToolbar('shapes', 'select');
      this._selectShape(token.id);
      const node = token.konvaShape;
      const scale = this._stage.scaleX();
      this._stage.position({
        x: this._stage.width() / 2 - (node.x() + node.width() / 2) * scale,
        y: this._stage.height() / 2 - (node.y() + node.height() / 2) * scale
      });
      this._stage.batchDraw();
      this._refreshShapePopover();
    } else {
      this.confirmCharacterToken({ detail: character }, { closeModal: false });
    }
  },

  openLinkedEffect(event) {
    event.preventDefault();
    const id = event.currentTarget.dataset.effectId;
    this._showEditModal(id, { tab: this._findShapeById(id)?.type === 'character' ? 'effects' : null });
  },

  _refreshCharacterEffects(shape, field) {
    if (field('characterSizeFields')) {
      field('characterSizeFields').hidden = shape.type !== 'character';
      if (shape.type === 'character') {
        field('characterSize').value = shape.sizeFeet;
        field('characterSizeLabel').textContent = `${shape.sizeFeet} ft`;
      }
    }
    if (!field('characterTabs')) return;
    field('characterTabs').hidden = shape.type !== 'character';
    const parent = shape.parentId && this._findShapeById(shape.parentId);
    field('parentCharacter').hidden = !parent;
    if (parent) {
      field('parentCharacter').textContent = `Moves with ${parent.name} · Edit character`;
      field('parentCharacter').dataset.effectId = parent.id;
    }
    if (shape.type !== 'character') return;
    const link = field('addCharacterEffect');
    const url = new URL(link.href, window.location.origin);
    url.searchParams.set('parent_id', shape.id);
    url.searchParams.set('token_size', shape.sizeFeet);
    link.href = url.href;
    field('characterEffects').innerHTML = this._shapes
      .filter((effect) => effect.parentId === shape.id && this._canEditShape(effect))
      .map(
        (effect) => `<button type="button" class="btn btn-outline-secondary text-start w-100 mb-2"
        data-action="konva-map#openLinkedEffect" data-effect-id="${this._escapeHtml(effect.id)}">
        <i class="fas fa-bullseye me-1" aria-hidden="true" style="color: ${this._escapeHtml(effect.color)}"></i>
        ${this._escapeHtml(effect.name)}</button>`
      )
      .join('');
  },

  _createCharacterTokenLabel(shape) {
    const node = shape.konvaShape;
    const group = new Konva.Group({ listening: false });
    group.add(
      new Konva.Text({
        x: node.x() - node.width() / 2,
        y: node.y() + node.height() + 3,
        width: node.width() * 2,
        text: shape.name,
        align: 'center',
        fontSize: 12,
        fill: '#ffffff',
        stroke: '#000000',
        strokeWidth: 0.5,
        wrap: 'none',
        ellipsis: true,
        listening: false
      })
    );
    return group;
  },

  changeCharacterSize(event) {
    const shape = this._findShapeById(event.currentTarget.dataset.shapeId);
    const feet = Number(event.currentTarget.value);
    if (!this._canEditShape(shape) || shape.type !== 'character') return;
    if (!Number.isInteger(feet) || feet < 5 || feet > 50 || feet % 5 !== 0 || feet === shape.sizeFeet) return;
    if (this._characterMove?.id === shape.id) this.cancelCharacterMove();
    this._lastCharacterMove = null;
    shape.sizeFeet = feet;
    this._syncAreaEffectGeometry();
    this._updateShapesList();
    this._refreshEditModal();
    this._save();
  },

  confirmCharacterToken(event, { closeModal = true } = {}) {
    if (this.readonlyValue || !this.gridEnabledValue || !this._stage) return;
    const token = event.detail;
    if (
      token.placeableType !== 'Character' ||
      !token.placeableId ||
      !Number.isFinite(token.sizeFeet) ||
      token.sizeFeet < 5
    )
      return;
    this._showToolbar('shapes', 'select');
    this._pendingCharacterToken = token;
    this._applyTool('character');
    if (this._mobileEditor) this.collapseMapControls();
    if (this.playerValue) this.saveStatusTarget.textContent = 'Click or tap a square to place your character.';
    if (closeModal) window.modal?.hideModal();
  },

  _clearCharacterPreview() {
    this._placingCharacter = false;
    this._characterPreview?.destroy();
    this._characterPreview = null;
    this._cursorLayer?.batchDraw();
  },

  _previewCharacterToken() {
    if (!this.gridEnabledValue || !this._pendingCharacterToken) return;
    const token = characterTokenAtPointer(
      this._getWorldPointerPosition(),
      this._pendingCharacterToken,
      this._areaEffectGrid()
    );
    const geometry = token && characterTokenGeometry(token, this._areaEffectGrid());
    if (!geometry) return;
    if (!this._characterPreview) {
      this._characterPreview = createCharacterTokenShape(token, {
        ...geometry,
        stroke: '#0d6efd',
        strokeWidth: 3,
        strokeScaleEnabled: false,
        dash: [8, 4],
        listening: false
      });
      this._cursorLayer.add(this._characterPreview);
    }
    this._characterPreview.setAttrs({ ...geometry, visible: true });
    this._cursorLayer.moveToTop();
    this._cursorLayer.batchDraw();
  },

  _placeCharacterToken() {
    if (this.readonlyValue || !this.gridEnabledValue || !this._pendingCharacterToken) return;
    const token = characterTokenAtPointer(
      this._getWorldPointerPosition(),
      this._pendingCharacterToken,
      this._areaEffectGrid()
    );
    const geometry = token && characterTokenGeometry(token, this._areaEffectGrid());
    if (!geometry) return;
    const shape = {
      ...SHAPE_DEFAULTS,
      ...token,
      id: `shape_${Date.now()}_${++this._shapeCounter}`,
      color: '#0d6efd',
      showLabels: false,
      fillOpacity: 1,
      creatorId: this.userIdValue,
      konvaShape: createCharacterTokenShape(token, {
        ...geometry,
        stroke: '#0d6efd',
        strokeWidth: 3,
        strokeScaleEnabled: false,
        listening: false
      })
    };
    this._shapes.push(shape);
    this._getShapeLayer(shape).add(shape.konvaShape);
    this._recreateLabels(shape);
    this._applyTool('select');
    this._selectShape(shape.id);
    this._save();
  },

  _moveCharacterToken(shape, position) {
    this._lastCharacterMove = null;
    const grid = this._areaEffectGrid();
    // Round the top-left position during drags; placement chooses the tapped cell.
    shape.gridColumn = Math.round((position.x - grid.offsetX) / grid.size);
    shape.gridRow = Math.round((position.y - grid.offsetY) / grid.size);
    shape.konvaShape.setAttrs(characterTokenGeometry(shape, grid));
    this._syncLinkedEffects?.(shape.id);
    this._refreshCharacterMovement?.();
  },

  _syncCharacterTokenGeometry() {
    for (const shape of this._shapes || []) {
      if (shape.type !== 'character') continue;
      const geometry = characterTokenGeometry(shape, this._areaEffectGrid());
      if (!geometry) continue;
      shape.konvaShape.setAttrs({ ...geometry, scaleX: 1, scaleY: 1 });
      this._recreateLabels(shape);
    }
    if (!this.gridEnabledValue && this._pendingCharacterToken) this._applyTool('drag');
    if (this._characterMove) {
      if (!this.gridEnabledValue) this.cancelCharacterMove();
      else this._drawCharacterDestination();
    }
  }
};
